import { apiClient } from '@/api/client';
import type { PaginatedResult } from '@/types/api';
import type {
  QueueSummary,
  QueueOverview, QueueMessage, QueueMessageDetail, QueueMessageParams,
  QueueMessageActionType, QueueControlActionType,
} from '@/features/queue/types';

/**
 * Mail Queue & Delivery Center — mail-admin-service /queue endpoints. The
 * legacy per-item Postfix client (list/act with a single queueId) was removed
 * in 4.9A: the Delivery Center's messages/action/control surface covers it, and
 * no page consumed it. The backend endpoints remain unchanged.
 */
export const queueApi = {
  summary: async (): Promise<QueueSummary> => (await apiClient.get<QueueSummary>('/queue/summary')).data,
  overview: async (): Promise<QueueOverview> =>
    (await apiClient.get<QueueOverview>('/queue/overview')).data,
  messages: async (params: QueueMessageParams): Promise<PaginatedResult<QueueMessage>> =>
    (await apiClient.get<PaginatedResult<QueueMessage>>('/queue/messages', { params })).data,
  // Encode the id so a value containing '/' or '?' can't reshape the request path.
  message: async (id: string): Promise<QueueMessageDetail> =>
    (await apiClient.get<QueueMessageDetail>(`/queue/messages/${encodeURIComponent(id)}`)).data,
  /** Row / bulk action on specific message(s). */
  action: async (action: QueueMessageActionType, queueIds: string[], targetQueue?: string): Promise<{ affected: number }> =>
    (await apiClient.post<{ affected: number }>('/queue/actions', { action, queueIds, targetQueue })).data,
  /** Pause / resume / flush a whole logical queue bucket. */
  control: async (action: QueueControlActionType, queue: string): Promise<{ queue: string; state: string }> =>
    (await apiClient.post<{ queue: string; state: string }>('/queue/control', { action, queue })).data,
};
